/*
 * Convert Image to Chart — preview geometry.
 *
 * Turns an `EditableChart` plus the selected tile into a flat list of drawing
 * primitives in a fixed viewBox. The maths lives here, away from the
 * component, for the two reasons this repo already separates them elsewhere
 * (see MapPlot's projectionEngine): it is the part worth unit-testing, and it
 * is the part that must behave identically for every chart kind.
 *
 * Everything is emitted in viewBox units. The component renders the result
 * into an `<svg viewBox=…>`, so the preview scales with the task pane instead
 * of being laid out at pixel sizes — a task pane can be dragged from ~250px to
 * half the screen, and the pane is the only width this feature ever gets.
 *
 * Colours arrive as 6-hex WITHOUT a leading '#' (the API's convention, kept
 * end to end so the same string feeds both this preview and the OOXML
 * builder); `swatch()` is the one place a '#' is added, for CSS.
 */

import type { ChartRenderKind } from "./chartTypeOptions";
import { axisNumberFormat, shouldShowLegend, type AxisScale, type ChartSeries, type EditableChart, type NumberFormatAffix, type SeriesRender } from "./convertChartApi";

/* ── Output primitives ─────────────────────────────────────────────────── */

export interface PreviewRect {
  readonly kind: "rect";
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly color: string;
}

export interface PreviewPath {
  readonly kind: "path";
  readonly d: string;
  readonly fill: string | null;
  readonly stroke: string | null;
  readonly strokeWidth: number;
  /** Fill opacity, for the translucent band under an area/radar series. */
  readonly opacity: number;
  /** Drawn in the gridline colour rather than a series colour (radar rings). */
  readonly muted: boolean;
}

export interface PreviewDot {
  readonly kind: "dot";
  readonly cx: number;
  readonly cy: number;
  readonly r: number;
  readonly color: string;
}

export interface PreviewAxisLine {
  readonly kind: "axis";
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  /** Gridlines are drawn lighter than the axis itself. */
  readonly muted: boolean;
}

export interface PreviewLabel {
  readonly kind: "label";
  readonly x: number;
  readonly y: number;
  readonly text: string;
  readonly anchor: "start" | "middle" | "end";
}

export type PreviewShape = PreviewRect | PreviewPath | PreviewDot | PreviewAxisLine | PreviewLabel;

export interface PreviewLegendEntry {
  readonly label: string;
  readonly color: string;
}

export interface ChartPreviewModel {
  readonly viewBoxWidth: number;
  readonly viewBoxHeight: number;
  readonly shapes: readonly PreviewShape[];
  readonly legend: readonly PreviewLegendEntry[];
  /** True when there was nothing plottable — the component shows its empty
   *  state rather than an axis frame with no marks in it. */
  readonly empty: boolean;
}

/* ── Canvas ────────────────────────────────────────────────────────────── */

export const VIEW_WIDTH = 320;
export const VIEW_HEIGHT = 170;
/** Font size for axis/category labels, in viewBox units. */
export const AXIS_LABEL_SIZE = 7;

const PLOT_LEFT = 34;
const PLOT_RIGHT = 8;
const PLOT_TOP = 10;
const PLOT_BOTTOM = 22;
const PLOT_WIDTH = VIEW_WIDTH - PLOT_LEFT - PLOT_RIGHT;
const PLOT_HEIGHT = VIEW_HEIGHT - PLOT_TOP - PLOT_BOTTOM;
const PLOT_BASE_Y = PLOT_TOP + PLOT_HEIGHT;

const GRID_LINE_COUNT = 4;
/** Category ticks are thinned rather than overlapped past this many. */
const MAX_CATEGORY_LABELS = 7;
/** Fraction of a category band the bars (all series together) occupy. */
const BAND_FILL = 0.68;

/**
 * Fallback series colours, used only where the extraction sampled none.
 *
 * Hardcoded rather than read from the deck theme on purpose: this previews
 * *extracted* data, and borrowing the deck's accent colours would imply the
 * rebuilt chart will use them, when an inserted chart takes PowerPoint's own
 * theme palette in the same slots instead.
 */
export const PREVIEW_FALLBACK_PALETTE: readonly string[] = [
  "1F5FAD",
  "E8873A",
  "3E9B6B",
  "B5453F",
  "6C55A6",
  "8A6A4F",
  "C05A9B",
  "5E7787",
];

/** 6-hex (no '#') → a CSS colour. The one place the '#' is added. */
export function swatch(hex: string): string {
  return `#${hex}`;
}

/** The colour for series `index`: what the model sampled, else the palette. */
export function seriesColor(index: number, extracted: string | null): string {
  if (extracted) return extracted;
  return PREVIEW_FALLBACK_PALETTE[index % PREVIEW_FALLBACK_PALETTE.length] ?? "1F5FAD";
}

/* ── Scales ────────────────────────────────────────────────────────────── */

interface ValueScale {
  readonly min: number;
  readonly max: number;
}

/**
 * Compact axis copy — a preview 320 units wide has no room for "1250000".
 *
 * `affix` puts the source's currency/percent symbol back, matching what the
 * inserted chart's number format produces, so the preview and the slide read
 * the same.
 */
export function formatAxisValue(value: number, affix: NumberFormatAffix | null = null): string {
  const text = compactValue(value);
  return affix ? `${affix.prefix}${text}${affix.suffix}` : text;
}

function compactValue(value: number): string {
  const abs = Math.abs(value);
  if (abs === 0) return "0";
  if (abs >= 1_000_000_000) return `${round1(value / 1_000_000_000)}B`;
  if (abs >= 1_000_000) return `${round1(value / 1_000_000)}M`;
  if (abs >= 1_000) return `${round1(value / 1_000)}k`;
  if (abs < 1) return String(Number(value.toFixed(2)));
  return String(Number(value.toFixed(abs < 10 ? 1 : 0)));
}

function round1(value: number): string {
  return String(Number(value.toFixed(1)));
}

/** Per-category stacked totals, split by sign so a series with negatives
 *  stacks downward instead of cancelling the positive part of the bar. */
function stackedExtent(chart: EditableChart): ValueScale {
  let min = 0;
  let max = 0;
  for (let i = 0; i < chart.categories.length; i++) {
    let positive = 0;
    let negative = 0;
    for (const series of chart.series) {
      const value = series.values[i];
      if (typeof value !== "number") continue;
      if (value >= 0) positive += value;
      else negative += value;
    }
    if (positive > max) max = positive;
    if (negative < min) min = negative;
  }
  return { min, max };
}

function plainExtent(series: readonly ChartSeries[]): ValueScale {
  let min = 0;
  let max = 0;
  for (const entry of series) {
    for (const value of entry.values) {
      if (typeof value !== "number") continue;
      if (value > max) max = value;
      if (value < min) min = value;
    }
  }
  return { min, max };
}

/**
 * The value scale the preview plots against.
 *
 * The extraction's own `y_axis` min/max wins when the model read them off the
 * source chart's tick labels — reproducing that scale is the whole point of it
 * reporting them, and auto-scaling instead would redraw a chart that
 * deliberately started at, say, 60 as though it started at 0.
 */
export function valueScale(chart: EditableChart, stacked: boolean): ValueScale {
  return scaleFor(stacked ? stackedExtent(chart) : plainExtent(chart.series), chart.yAxis);
}

/**
 * The scale for ONE axis: the model's own min/max when it read them, else the
 * extent of just the series plotted against that axis.
 *
 * Per-axis rather than per-chart because a combo's secondary series live on a
 * different scale entirely — percentages beside a currency axis, typically.
 * Measuring them together squashes one of the two into the floor of the plot,
 * which is exactly what a dual-axis source chart uses two axes to avoid.
 */
function scaleFor(extent: ValueScale, axis: AxisScale | null): ValueScale {
  const min = typeof axis?.min === "number" ? axis.min : extent.min;
  const max = typeof axis?.max === "number" ? axis.max : extent.max;
  // A flat all-zero (or inverted) scale would divide by zero in toY. The
  // finite checks are not redundant with that: `typeof NaN === "number"`, so a
  // non-finite bound would otherwise pass straight through and turn every
  // projected coordinate into NaN.
  const base = Number.isFinite(min) ? min : 0;
  if (!Number.isFinite(max) || max <= base) return { min: base, max: base + 1 };
  return { min: base, max };
}

/** True when any series is plotted against the right-hand axis. */
function hasSecondarySeries(chart: EditableChart): boolean {
  return chart.series.some((series) => series.axis === "secondary");
}

/**
 * Value → y, clamped to the plot band.
 *
 * Clamping matters because the scale can come from the *image's* axis labels
 * while the values come from the model's reading of the marks — a point above
 * the axis maximum is a realistic disagreement, and an unclamped one would
 * paint over the labels or leave the viewBox entirely.
 */
function toY(value: number, scale: ValueScale): number {
  const ratio = (value - scale.min) / (scale.max - scale.min);
  const y = PLOT_BASE_Y - ratio * PLOT_HEIGHT;
  return Math.min(PLOT_BASE_Y, Math.max(PLOT_TOP, y));
}

/** x-centre of category `index` within the plot. */
function categoryCentre(index: number, count: number): number {
  return PLOT_LEFT + (PLOT_WIDTH / Math.max(count, 1)) * (index + 0.5);
}

/* ── Frame ─────────────────────────────────────────────────────────────── */

function frameShapes(chart: EditableChart, scale: ValueScale): PreviewShape[] {
  const shapes: PreviewShape[] = [];
  const affix = axisNumberFormat(chart);

  for (let i = 0; i <= GRID_LINE_COUNT; i++) {
    const value = scale.min + ((scale.max - scale.min) * i) / GRID_LINE_COUNT;
    const y = toY(value, scale);
    shapes.push(
      { kind: "axis", x1: PLOT_LEFT, y1: y, x2: PLOT_LEFT + PLOT_WIDTH, y2: y, muted: i !== 0 },
      { kind: "label", x: PLOT_LEFT - 4, y: y + 2.5, text: formatAxisValue(value, affix), anchor: "end" },
    );
  }

  const step = Math.max(1, Math.ceil(chart.categories.length / MAX_CATEGORY_LABELS));
  chart.categories.forEach((category, index) => {
    if (index % step !== 0) return;
    shapes.push({
      kind: "label",
      x: categoryCentre(index, chart.categories.length),
      y: PLOT_BASE_Y + 10,
      text: category,
      anchor: "middle",
    });
  });

  return shapes;
}

/** Category axis title under the plot. The value-axis title is dropped — a
 *  rotated label costs more width than a 320-unit preview can spare. */
function axisTitleShapes(chart: EditableChart): PreviewShape[] {
  if (!chart.xAxisTitle) return [];
  return [{ kind: "label", x: PLOT_LEFT + PLOT_WIDTH / 2, y: VIEW_HEIGHT - 3, text: chart.xAxisTitle, anchor: "middle" }];
}

/* ── Categorical marks ─────────────────────────────────────────────────── */

/** A series kept together with its position in the original list, so its
 *  palette slot survives being filtered down to one render kind. */
interface IndexedSeries {
  readonly series: ChartSeries;
  readonly index: number;
}

function indexedSeries(chart: EditableChart): IndexedSeries[] {
  return chart.series.map((series, index) => ({ series, index }));
}

/** How one series is drawn inside a combo, defaulting to the chart's base. */
function comboRenderOf(series: ChartSeries, base: SeriesRender): SeriesRender {
  return series.renderAs ?? base;
}

function barShapes(chart: EditableChart, scale: ValueScale, stacked: boolean, drawn: IndexedSeries[]): PreviewShape[] {
  if (drawn.length === 0) return [];

  const shapes: PreviewShape[] = [];
  const bandWidth = PLOT_WIDTH / Math.max(chart.categories.length, 1);
  const groupWidth = bandWidth * BAND_FILL;
  const barWidth = stacked ? groupWidth : groupWidth / drawn.length;
  const zeroValue = Math.max(scale.min, 0);

  for (let categoryIndex = 0; categoryIndex < chart.categories.length; categoryIndex++) {
    const bandStart = PLOT_LEFT + bandWidth * categoryIndex + (bandWidth - groupWidth) / 2;
    // Accumulated in value space, split by sign, so a stack with negatives
    // grows in both directions from the baseline rather than self-cancelling.
    let positiveTop = zeroValue;
    let negativeBottom = zeroValue;

    drawn.forEach(({ series, index }, drawIndex) => {
      const value = series.values[categoryIndex];
      if (typeof value !== "number") return;

      let fromValue: number;
      let toValue: number;
      if (stacked) {
        if (value >= 0) {
          fromValue = positiveTop;
          positiveTop += value;
          toValue = positiveTop;
        } else {
          fromValue = negativeBottom;
          negativeBottom += value;
          toValue = negativeBottom;
        }
      } else {
        fromValue = zeroValue;
        toValue = value;
      }

      const y1 = toY(fromValue, scale);
      const y2 = toY(toValue, scale);
      shapes.push({
        kind: "rect",
        x: stacked ? bandStart : bandStart + barWidth * drawIndex,
        y: Math.min(y1, y2),
        width: Math.max(barWidth - (stacked ? 0 : 1), 1),
        // A value that rounds to nothing still deserves a visible sliver.
        height: Math.max(Math.abs(y2 - y1), 0.6),
        color: swatch(seriesColor(index, series.color)),
      });
    });
  }

  return shapes;
}

function polylinePath(points: ReadonlyArray<{ readonly x: number; readonly y: number }>): string {
  return points.map((point, index) => `${index === 0 ? "M" : "L"}${point.x.toFixed(2)} ${point.y.toFixed(2)}`).join(" ");
}

function lineShapes(chart: EditableChart, scale: ValueScale, drawn: IndexedSeries[]): PreviewShape[] {
  const shapes: PreviewShape[] = [];

  for (const { series, index } of drawn) {
    const points: Array<{ x: number; y: number }> = [];
    for (let i = 0; i < chart.categories.length; i++) {
      const value = series.values[i];
      if (typeof value !== "number") continue;
      points.push({ x: categoryCentre(i, chart.categories.length), y: toY(value, scale) });
    }
    if (points.length === 0) continue;

    const color = swatch(seriesColor(index, series.color));
    if (points.length === 1) {
      // One readable point draws no line, so it gets a dot to stand on.
      const only = points[0];
      if (only) shapes.push({ kind: "dot", cx: only.x, cy: only.y, r: 2, color });
      continue;
    }
    shapes.push({
      kind: "path",
      d: polylinePath(points),
      fill: null,
      stroke: color,
      strokeWidth: 1.6,
      opacity: 1,
      muted: false,
    });
  }

  return shapes;
}

function areaShapes(chart: EditableChart, scale: ValueScale, stacked: boolean): PreviewShape[] {
  const shapes: PreviewShape[] = [];
  const zeroValue = Math.max(scale.min, 0);
  const running: number[] = new Array<number>(chart.categories.length).fill(zeroValue);

  chart.series.forEach((series, index) => {
    const top: Array<{ x: number; y: number }> = [];
    const bottom: Array<{ x: number; y: number }> = [];

    for (let i = 0; i < chart.categories.length; i++) {
      const value = series.values[i];
      if (typeof value !== "number") continue;
      const x = categoryCentre(i, chart.categories.length);
      const previous = running[i] ?? zeroValue;
      const total = stacked ? previous + value : value;
      if (stacked) running[i] = total;
      top.push({ x, y: toY(total, scale) });
      bottom.push({ x, y: toY(stacked ? previous : zeroValue, scale) });
    }
    if (top.length === 0) return;

    const color = swatch(seriesColor(index, series.color));
    const closing = bottom
      .slice()
      .reverse()
      .map((point) => `L${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
      .join(" ");
    shapes.push({
      kind: "path",
      d: `${polylinePath(top)} ${closing} Z`,
      fill: color,
      stroke: color,
      strokeWidth: 1,
      opacity: 0.35,
      muted: false,
    });
  });

  return shapes;
}

/* ── Pie / doughnut / radar geometry ───────────────────────────────────── */

const CENTRE_X = VIEW_WIDTH / 2;
const CENTRE_Y = VIEW_HEIGHT / 2;
const PIE_RADIUS = 62;
const DOUGHNUT_INNER_RADIUS = 34;
const RADAR_RADIUS = 58;
const RADAR_RINGS = 3;
/** Below three spokes a radar is a line, not a shape. */
const RADAR_MIN_AXES = 3;

function polarPoint(radius: number, angle: number): { x: number; y: number } {
  return { x: CENTRE_X + radius * Math.cos(angle), y: CENTRE_Y + radius * Math.sin(angle) };
}

/** One slice as an SVG arc — a ring segment when `innerRadius` > 0. */
function slicePath(startAngle: number, endAngle: number, innerRadius: number): string {
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  const outerStart = polarPoint(PIE_RADIUS, startAngle);
  const outerEnd = polarPoint(PIE_RADIUS, endAngle);

  if (innerRadius <= 0) {
    return (
      `M${CENTRE_X} ${CENTRE_Y} L${outerStart.x.toFixed(2)} ${outerStart.y.toFixed(2)} ` +
      `A${PIE_RADIUS} ${PIE_RADIUS} 0 ${largeArc} 1 ${outerEnd.x.toFixed(2)} ${outerEnd.y.toFixed(2)} Z`
    );
  }

  const innerStart = polarPoint(innerRadius, endAngle);
  const innerEnd = polarPoint(innerRadius, startAngle);
  return (
    `M${outerStart.x.toFixed(2)} ${outerStart.y.toFixed(2)} ` +
    `A${PIE_RADIUS} ${PIE_RADIUS} 0 ${largeArc} 1 ${outerEnd.x.toFixed(2)} ${outerEnd.y.toFixed(2)} ` +
    `L${innerStart.x.toFixed(2)} ${innerStart.y.toFixed(2)} ` +
    `A${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerEnd.x.toFixed(2)} ${innerEnd.y.toFixed(2)} Z`
  );
}

/**
 * Pie/doughnut slices from the FIRST series only.
 *
 * A pie has exactly one dimension; when an extraction carries several series
 * (because the user re-picked Pie for a multi-series chart) the rest are
 * ignored rather than summed — which is what PowerPoint does with the same
 * data, and keeps the preview honest about the inserted result.
 */
function pieShapes(chart: EditableChart, innerRadius: number): {
  readonly shapes: PreviewShape[];
  readonly legend: PreviewLegendEntry[];
} {
  const series = chart.series[0];
  const shapes: PreviewShape[] = [];
  const legend: PreviewLegendEntry[] = [];
  if (!series) return { shapes, legend };

  // Negative slices have no meaning in a pie; magnitudes are what gets plotted.
  const magnitudes = chart.categories.map((_, index) => {
    const value = series.values[index];
    return typeof value === "number" ? Math.abs(value) : 0;
  });
  const total = magnitudes.reduce((sum, value) => sum + value, 0);
  if (total <= 0) return { shapes, legend };

  let angle = -Math.PI / 2; // 12 o'clock, where PowerPoint starts its first slice
  chart.categories.forEach((category, index) => {
    const magnitude = magnitudes[index] ?? 0;
    if (magnitude <= 0) return;
    const sweep = (magnitude / total) * Math.PI * 2;
    const color = swatch(chart.sliceColors?.[index] ?? seriesColor(index, null));
    shapes.push({
      kind: "path",
      d: slicePath(angle, angle + sweep, innerRadius),
      fill: color,
      stroke: null,
      strokeWidth: 0,
      opacity: 1,
      muted: false,
    });
    legend.push({ label: category || `Slice ${index + 1}`, color });
    angle += sweep;
  });

  return { shapes, legend };
}

function radarShapes(chart: EditableChart, scale: ValueScale): PreviewShape[] {
  const axes = chart.categories.length;
  if (axes < RADAR_MIN_AXES) return [];

  const shapes: PreviewShape[] = [];
  const angleFor = (index: number) => -Math.PI / 2 + (index / axes) * Math.PI * 2;
  const radiusFor = (value: number) =>
    Math.max(0, Math.min(1, (value - scale.min) / (scale.max - scale.min))) * RADAR_RADIUS;

  for (let ring = 1; ring <= RADAR_RINGS; ring++) {
    const radius = (RADAR_RADIUS * ring) / RADAR_RINGS;
    const points = chart.categories.map((_, index) => polarPoint(radius, angleFor(index)));
    shapes.push({
      kind: "path",
      d: `${polylinePath(points)} Z`,
      fill: null,
      stroke: null,
      strokeWidth: 0.5,
      opacity: 1,
      muted: true,
    });
  }

  chart.series.forEach((series, seriesIndex) => {
    const points: Array<{ x: number; y: number }> = [];
    chart.categories.forEach((_, index) => {
      const value = series.values[index];
      if (typeof value !== "number") return;
      points.push(polarPoint(radiusFor(value), angleFor(index)));
    });
    if (points.length < RADAR_MIN_AXES) return;
    const color = swatch(seriesColor(seriesIndex, series.color));
    shapes.push({
      kind: "path",
      d: `${polylinePath(points)} Z`,
      fill: color,
      stroke: color,
      strokeWidth: 1.4,
      opacity: 0.22,
      muted: false,
    });
  });

  return shapes;
}

/* ── Scatter ───────────────────────────────────────────────────────────── */

/**
 * Scatter points.
 *
 * `categories` carries the numeric x-values as strings on a scatter chart
 * (ChartType.scatter's own docstring says so). A category that won't parse
 * falls back to its ordinal position, so a partly-labelled axis still plots
 * every point rather than dropping half the series.
 */
function scatterShapes(chart: EditableChart, scale: ValueScale): PreviewShape[] {
  const xs = chart.categories.map((category, index) => {
    const parsed = Number(category);
    return Number.isFinite(parsed) ? parsed : index;
  });
  const xMin = Math.min(...xs);
  const xMaxRaw = Math.max(...xs);
  const xMax = xMaxRaw > xMin ? xMaxRaw : xMin + 1;

  const shapes: PreviewShape[] = [];
  chart.series.forEach((series, seriesIndex) => {
    const color = swatch(seriesColor(seriesIndex, series.color));
    xs.forEach((x, index) => {
      const value = series.values[index];
      if (typeof value !== "number") return;
      const ratio = (x - xMin) / (xMax - xMin);
      shapes.push({ kind: "dot", cx: PLOT_LEFT + ratio * PLOT_WIDTH, cy: toY(value, scale), r: 2.2, color });
    });
  });
  return shapes;
}

/* ── Entry point ───────────────────────────────────────────────────────── */

function seriesLegend(chart: EditableChart): PreviewLegendEntry[] {
  return chart.series.map((series, index) => ({
    label: series.name || `Series ${index + 1}`,
    color: swatch(seriesColor(index, series.color)),
  }));
}

/** Every model shares one canvas; only the contents differ. */
function previewModel(
  shapes: readonly PreviewShape[],
  legend: readonly PreviewLegendEntry[],
  empty: boolean,
): ChartPreviewModel {
  return { viewBoxWidth: VIEW_WIDTH, viewBoxHeight: VIEW_HEIGHT, shapes, legend, empty };
}

/** Is this series plotted against the right-hand axis? */
function isSecondary({ series }: IndexedSeries): boolean {
  return series.axis === "secondary";
}

/** Is this series drawn as a line inside a combo? */
function isComboLine({ series }: IndexedSeries): boolean {
  return comboRenderOf(series, "column") === "line";
}

/** True when the tile stacks and the kind can express stacking. */
function isStackedRender(chart: EditableChart, render: ChartRenderKind): boolean {
  return chart.isStacked && (render === "column" || render === "bar" || render === "area");
}

/**
 * The left-hand axis's scale. On a combo it measures only the series that use
 * that axis — see scaleFor.
 */
function primaryScale(chart: EditableChart, render: ChartRenderKind, stacked: boolean): ValueScale {
  if (stacked) return valueScale(chart, true);
  const measured =
    render === "combo" && hasSecondarySeries(chart)
      ? chart.series.filter((series) => series.axis !== "secondary")
      : chart.series;
  return scaleFor(plainExtent(measured), chart.yAxis);
}

/**
 * Combo marks, each axis group measured on its own scale.
 *
 * Bars are emitted before lines so an overlaid line series stays readable on
 * top of them.
 */
function comboMarks(chart: EditableChart, primary: ValueScale): PreviewShape[] {
  const all = indexedSeries(chart);
  const secondary = all.filter(isSecondary);
  const groups: ReadonlyArray<readonly [IndexedSeries[], ValueScale]> = [
    [all.filter((entry) => !isSecondary(entry)), primary],
    [secondary, scaleFor(plainExtent(secondary.map(({ series }) => series)), chart.secondaryYAxis)],
  ];

  return [
    ...groups.flatMap(([entries, scale]) => barShapes(chart, scale, false, entries.filter((e) => !isComboLine(e)))),
    ...groups.flatMap(([entries, scale]) => lineShapes(chart, scale, entries.filter(isComboLine))),
  ];
}

/** The marks for every kind that plots against an x/y frame. */
function cartesianMarks(
  chart: EditableChart,
  render: ChartRenderKind,
  scale: ValueScale,
  stacked: boolean,
): PreviewShape[] {
  if (render === "combo") return comboMarks(chart, scale);
  if (render === "column" || render === "bar") return barShapes(chart, scale, stacked, indexedSeries(chart));
  if (render === "area") return areaShapes(chart, scale, stacked);
  if (render === "line") return lineShapes(chart, scale, indexedSeries(chart));
  return scatterShapes(chart, scale);
}

function piePreview(chart: EditableChart, render: ChartRenderKind): ChartPreviewModel {
  const { shapes, legend } = pieShapes(chart, render === "doughnut" ? DOUGHNUT_INNER_RADIUS : 0);
  return previewModel(shapes, shouldShowLegend(chart) ? legend : [], shapes.length === 0);
}

function radarPreview(chart: EditableChart, scale: ValueScale): ChartPreviewModel {
  const shapes = radarShapes(chart, scale);
  return previewModel(
    shapes,
    shouldShowLegend(chart) ? seriesLegend(chart) : [],
    // The rings always draw; only a series polygon counts as content.
    !shapes.some((shape) => shape.kind === "path" && !shape.muted),
  );
}

/**
 * Build the preview for `chart` drawn as `render`.
 *
 * `render` comes from the selected tile rather than `chart.chartType`, because
 * two tiles (Radar, Combo) have no enum member of their own and three more
 * differ from their base only by `is_stacked` — see chartTypeOptions.ts.
 *
 * The two early returns are the kinds that plot without an x/y frame;
 * everything past them is cartesian and gets one.
 *
 * NOTE on Bar: the preview draws a horizontal-bar chart with vertical bars.
 * Transposing the whole frame would double this module for a thumbnail whose
 * job is to confirm *the data*; the chart inserted into the deck is a true
 * horizontal bar (`barDir: "bar"`).
 */
export function buildChartPreview(chart: EditableChart, render: ChartRenderKind): ChartPreviewModel {
  if (chart.categories.length === 0 || chart.series.length === 0) return previewModel([], [], true);
  if (render === "pie" || render === "doughnut") return piePreview(chart, render);

  const stacked = isStackedRender(chart, render);
  const scale = primaryScale(chart, render, stacked);
  if (render === "radar") return radarPreview(chart, scale);

  const marks = cartesianMarks(chart, render, scale, stacked);
  return previewModel(
    [...frameShapes(chart, scale), ...axisTitleShapes(chart), ...marks],
    shouldShowLegend(chart) ? seriesLegend(chart) : [],
    marks.length === 0,
  );
}
